import { useI18n } from '../../../i18n';
import { cn } from '../../../utils/cn';

// ============================================
// Types
// ============================================

export type SupplierStatus = 'active' | 'pending' | 'inactive';

interface SupplierStatusBadgeProps {
  status?: SupplierStatus | string | null;
  className?: string;
}

// ============================================
// Styles
// ============================================

const statusConfig: Record<
  SupplierStatus,
  { bg: string; dot: string; text: string; key: string }
> = {
  active: {
    bg: 'bg-[#ECFDF5]',
    dot: 'bg-[#10B981]',
    text: 'text-[#047857]',
    key: 'suppliersActive',
  },
  pending: {
    bg: 'bg-[#FFFBEB]',
    dot: 'bg-[#F59E0B]',
    text: 'text-[#B45309]',
    key: 'pending',
  },
  inactive: {
    bg: 'bg-[#FEF2F2]',
    dot: 'bg-[#EF4444]',
    text: 'text-[#DC2626]',
    key: 'suppliersInactive',
  },
};

const fallbackStatusConfig = {
  bg: 'bg-[#FEF2F2]',
  dot: 'bg-[#EF4444]',
  text: 'text-[#DC2626]',
  key: 'suppliersInactive',
};

// ============================================
// Component
// ============================================

export function SupplierStatusBadge({ status, className }: SupplierStatusBadgeProps) {
  const { t } = useI18n();
  const normalizedStatus = typeof status === 'string' ? status.trim().toLowerCase() : '';
  const config =
    statusConfig[normalizedStatus as SupplierStatus] ??
    statusConfig.inactive ??
    fallbackStatusConfig;

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1',
        config.bg,
        className
      )}
    >
      <span className={cn('h-1.5 w-1.5 rounded-full', config.dot)} />
      <span className={cn('text-xs font-bold', config.text)}>{t(config.key)}</span>
    </span>
  );
}
